import React, { useEffect, useState } from 'react'
import LazyLoad from 'react-lazyload'
import { getReviewsByProductId } from '@api/apiCalls'
import trackError from '@helpers/trackError'
import { resizeShopifyImage } from '@helpers/shopify'
import { getBadgeTheme, getProductTag } from '@helpers/products'
import Badge from '@components/ui/Badge'
import { PRODUCT_COLOUR_TAGS, PRODUCT_TYPE_TAGS } from '@consts/index'
import { ProductCardProduct } from './types'
import ProductHeader from './ProductHeader'
import styles from './styles.module.scss'

const VALID_PRODUCT_CARD_TAGS = [...PRODUCT_COLOUR_TAGS, ...PRODUCT_TYPE_TAGS]


function ProductTag({ tags }: { tags: string }) {
  const productTag = getProductTag(tags.split(', '), VALID_PRODUCT_CARD_TAGS)

  if (!productTag) return null

  return (
    <div className={styles.ProductCard__badgeWrapper}>
      <Badge
        title={productTag}
        themeColour={getBadgeTheme(productTag)}
        small
        {...(productTag?.toLowerCase() === 'pre-order' && {
          iconName: 'alarm'
        })}
      />
    </div>
  )
}

type ProductCardProps = Pick<
  ProductCardProduct,
  | 'id'
  | 'title'
  | 'byline'
  | 'tags'
  | 'image'
  | 'imageHover'
  | 'price'
  | 'fullPrice'
> & {
  className: string
}

function ProductCard({
  id,
  title,
  byline,
  tags,
  image,
  imageHover,
  price,
  fullPrice,
  className
}: ProductCardProps) {
  const [productReviews, setProductReviews] = useState(undefined)

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const reviews = await getReviewsByProductId(id)
        setProductReviews(reviews)
      } catch (error) {
        trackError(new Error('Error with the Yotpo response'), {
          error: error as Error
        })
      }
    }
    if (id) {
      fetchReviews()
    }
  }, [id])

  return (
    <div className={`${styles.ProductCard} ${className}`}>
      <div className={styles.ProductCard__imageWrapper}>
        <ProductTag tags={tags} />
        <LazyLoad height={410} once>
          {imageHover && (
            <img
              className={`${styles.ProductCard__secondaryImage}`}
              src={resizeShopifyImage(imageHover, {
                width: '565',
                height: '565',
                crop: 'center'
              })}
              alt={`${title} ${byline} Secondary`}
              width="283"
              height="283"
            />
          )}
          <img
            src={resizeShopifyImage(image, {
              width: '565',
              height: '565',
              crop: 'center'
            })}
            alt={`${title} ${byline} Primary`}
            width="283"
            height="283"
          />
        </LazyLoad>
      </div>
      {!!title && (
        <ProductHeader
          title={title}
          byline={byline}
          price={price}
          fullPrice={fullPrice}
          starRatings={productReviews}
        />
      )}
    </div>
  )
}

export default ProductCard
